import React, { useState, useEffect } from 'react';
import { MapPin, Search, Grid3X3, Layers, Box, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const LocationPicker = ({ selectedLocation, onLocationChange, required = false, multiple = false }) => {
  console.log('LocationPicker - Props:', { selectedLocation, multiple });

  const [racks, setRacks] = useState([]);
  const [occupancyData, setOccupancyData] = useState({});
  const [showPicker, setShowPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRack, setSelectedRack] = useState(null);
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [hoveredLocation, setHoveredLocation] = useState(null);

  // Monitor changes to selectedLocation prop
  useEffect(() => {
    console.log('LocationPicker - selectedLocation changed:', selectedLocation);
    if (selectedLocation && selectedLocation.includes(',')) {
      console.log('LocationPicker - Multiple locations detected:', selectedLocation.split(','));
    }
  }, [selectedLocation]);

  // Fetch real warehouse structure data
  useEffect(() => {
    const fetchWarehouseStructure = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/structure/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const racksData = Array.isArray(response.data) ? response.data : 
                         response.data.results ? response.data.results : [];
        setRacks(racksData);
      } catch (error) {
        console.error('Error fetching warehouse structure:', error);
        // Fallback to mock data
        setRacks([
          {
            id: 1,
            name: 'A',
            shelves: 5,
            containers: 4,
            items: 12
          },
          {
            id: 2,
            name: 'B',
            shelves: 6,
            containers: 3,
            items: 8
          }
        ]);
      }
    };

    const fetchOccupancyData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/structure/occupancy/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        // Convert array to object for faster lookup: {locationCode: isOccupied}
        const occupancyDataArray = Array.isArray(response.data) ? response.data : 
                                  response.data.results ? response.data.results : [];
        const occupancyMap = {};
        occupancyDataArray.forEach(item => {
          occupancyMap[item.location] = true;
        });
        setOccupancyData(occupancyMap);
      } catch (error) {
        console.error('Error fetching occupancy data:', error);
      }
    };

    fetchWarehouseStructure();
    fetchOccupancyData();
  }, []);

  const generateLocationCode = (rackName, shelf, container) => {
    const shelfStr = shelf.toString().padStart(2, '0');
    const containerStr = String.fromCharCode(65 + container);
    return `${rackName}-${shelfStr}-${containerStr}`;
  };

  const getAllLocations = () => {
    const locations = [];
    racks.forEach(rack => {
      for (let shelf = 1; shelf <= rack.shelves; shelf++) {
        for (let container = 0; container < rack.containers; container++) {
          const code = generateLocationCode(rack.name, shelf, container);
          locations.push({
            code,
            rack: rack.name,
            shelf,
            container: String.fromCharCode(65 + container),
            rackId: rack.id,
            occupied: occupancyData[code] || false // Real occupancy data
          });
        }
      }
    });
    return locations;
  };

  const filteredLocations = getAllLocations().filter(location =>
    location.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper functions for multiple selection
  const getSelectedLocations = () => {
    if (!selectedLocation) return [];
    if (!multiple) return [selectedLocation];
    // Split by comma and trim each location, filter out empty strings
    return selectedLocation.split(',').map(loc => loc.trim()).filter(loc => loc);
  };

  const toggleLocation = (locationCode) => {
    if (!multiple) {
      onLocationChange(locationCode);
      setShowPicker(false);
      setSelectedRack(null);
      setSelectedShelf(null);
      return;
    }

    const currentLocations = getSelectedLocations();
    console.log('LocationPicker - Current locations before toggle:', currentLocations);
    const isSelected = currentLocations.includes(locationCode);
    console.log(`LocationPicker - Location ${locationCode} is ${isSelected ? 'already selected' : 'not selected'}`);

    let newLocations;
    if (isSelected) {
      // Remove location
      newLocations = currentLocations.filter(loc => loc !== locationCode);
    } else {
      // Add location
      newLocations = [...currentLocations, locationCode];
    }

    const newLocationString = newLocations.join(',');
    console.log(`LocationPicker - ${isSelected ? 'Removed' : 'Added'} location: ${locationCode}`);
    console.log('LocationPicker - New locations:', newLocations);
    console.log('LocationPicker - New location string:', newLocationString);
    onLocationChange(newLocationString);
  };

  const selectLocation = (locationCode) => {
    toggleLocation(locationCode);
  };

  const LocationGrid = ({ rack }) => {
    return (
      <div className="space-y-2">
        {Array.from({ length: rack.shelves }, (_, shelfIndex) => {
          const shelfNum = shelfIndex + 1;
          return (
            <div key={shelfIndex} className="flex items-center space-x-2 mb-1">
              <div className="w-12 text-right">
                <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                  {shelfNum.toString().padStart(2, '0')}
                </span>
              </div>
              {Array.from({ length: rack.containers }, (_, containerIndex) => {
                const locationCode = generateLocationCode(rack.name, shelfNum, containerIndex);
                const isOccupied = occupancyData[locationCode] || false; // Real occupancy data
                const selectedLocations = getSelectedLocations();
                const isSelected = selectedLocations.includes(locationCode);
                const isHovered = hoveredLocation === locationCode;
                
                return (
                  <button
                    type="button"
                    key={containerIndex}
                    onClick={() => selectLocation(locationCode)}
                    onMouseEnter={() => setHoveredLocation(locationCode)}
                    onMouseLeave={() => setHoveredLocation(null)}
                    className={`w-10 h-8 text-xs font-mono font-bold rounded border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-600 shadow-lg transform scale-105'
                        : isHovered
                        ? 'bg-blue-100 border-blue-300 transform scale-105 shadow-md'
                        : isOccupied
                        ? 'bg-yellow-100 border-yellow-300 text-yellow-700 hover:bg-yellow-200 hover:border-yellow-400 hover:shadow-md transform hover:scale-105'
                        : 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200 hover:border-green-400 hover:shadow-md transform hover:scale-105'
                    }`}
                    title={`Click to select ${locationCode} - ${isOccupied ? 'In Use (can add more)' : 'Empty'}`}
                  >
                    {String.fromCharCode(65 + containerIndex)}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Location
      </label>
      
      {/* Location Input */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className={`w-full p-3 border rounded-lg text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            selectedLocation ? 'border-green-500 bg-green-50' : 'border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 min-w-0">
              <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
              <span className={`${selectedLocation ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                {selectedLocation ? (
                  multiple ? (
                    getSelectedLocations().length > 0 ?
                      `${getSelectedLocations().length} location${getSelectedLocations().length > 1 ? 's' : ''} selected` :
                      'Select locations...'
                  ) : selectedLocation
                ) : `Select location${multiple ? 's' : ''}...`}
              </span>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${showPicker ? 'rotate-90' : ''} flex-shrink-0`} />
          </div>
        </button>
      </div>

      {/* Location Picker Modal */}
      {showPicker && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Select Location</h4>
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="flex">
            {/* Rack List */}
            <div className="w-1/4 border-r border-gray-200 bg-gray-50">
              <div className="p-2 bg-gray-100 border-b border-gray-200">
                <h4 className="text-xs font-semibold text-gray-700 text-center">Racks</h4>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                {racks.map(rack => (
                  <button
                    type="button"
                    key={rack.id}
                    onClick={() => setSelectedRack(rack)}
                    className={`w-full py-2 px-2 text-left hover:bg-blue-50 border-b border-gray-200 transition-colors ${
                      selectedRack?.id === rack.id ? 'bg-blue-100 border-l-4 border-l-blue-500 font-semibold' : ''
                    }`}
                  >
                    <div className="text-xs font-medium text-gray-900">Rack {rack.name}</div>
                    <div className="text-xs text-gray-500">{rack.shelves}×{rack.containers}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Location Grid or Search Results */}
            <div className="flex-1">
              {searchTerm ? (
                /* Search Results */
                <div>
                  <div className="p-2 bg-gray-100 border-b border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-700">
                      Search Results ({filteredLocations.length})
                    </h4>
                  </div>
                  <div className="p-2 max-h-[280px] overflow-y-auto">
                    <div className="grid grid-cols-4 gap-2">
                      {filteredLocations.map(location => {
                        const selectedLocations = getSelectedLocations();
                        const isSelected = selectedLocations.includes(location.code);
                        return (
                          <button
                            type="button"
                            key={location.code}
                            onClick={() => selectLocation(location.code)}
                            className={`p-2 text-xs font-mono font-bold rounded border-2 text-center ${
                              isSelected
                                ? 'bg-blue-500 text-white border-blue-600'
                                : location.occupied
                                ? 'bg-yellow-100 border-yellow-300 text-yellow-700 hover:bg-yellow-200'
                                : 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200'
                            }`}
                            title={location.occupied ? 'In Use (can add more)' : 'Empty'}
                          >
                            {location.code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : selectedRack ? (
                /* Location Grid */
                <div>
                  <div className="p-2 bg-gray-100 border-b border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-700">
                      Rack {selectedRack.name} - Select Location
                    </h4>
                  </div>
                  <div className="p-2 max-h-[280px] overflow-y-auto">
                    <LocationGrid rack={selectedRack} />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">
                  <p>← Select a rack to view locations</p>
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="p-2 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></div>
                <span className="text-gray-600">Empty</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded mr-1"></div>
                <span className="text-gray-600">In Use</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 border border-blue-600 rounded mr-1"></div>
                <span className="text-gray-600">Selected</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Location Info */}
      {selectedLocation && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm">
          {multiple ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center text-green-700">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span className="font-semibold">
                    {getSelectedLocations().length} location{getSelectedLocations().length > 1 ? 's' : ''} selected
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onLocationChange('')}
                  className="px-2 py-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded text-xs"
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {getSelectedLocations().map((location, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded font-mono text-xs"
                  >
                    {location}
                    <button
                      type="button"
                      onClick={() => toggleLocation(location)}
                      className="ml-1 text-green-500 hover:text-green-700"
                      title={`Remove ${location}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center text-green-700">
                <MapPin className="h-4 w-4 mr-2" />
                <div>
                  <div className="font-mono font-bold text-lg">{selectedLocation}</div>
                  <div className="text-xs text-green-600">
                    {selectedLocation.includes('-') ? (
                      <>
                        Rack: {selectedLocation.split('-')[0]} |
                        Shelf: {selectedLocation.split('-')[1]} |
                        Container: {selectedLocation.split('-')[2]}
                      </>
                    ) : (
                      'Custom location'
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onLocationChange('')}
                className="px-2 py-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded text-xs"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;